import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "variable-picker" }
const _hoisted_2 = { class: "mb-3" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "col-md-6" }
const _hoisted_5 = { class: "col-md-6" }
const _hoisted_6 = ["value"]
const _hoisted_7 = {
  key: 0,
  class: "mb-3"
}
const _hoisted_8 = { class: "selected-variables" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  class: "variables-list",
  style: {"max-height":"300px","overflow-y":"auto"}
}
const _hoisted_11 = {
  key: 0,
  class: "text-center text-muted py-3"
}
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "card-body p-3" }
const _hoisted_14 = { class: "d-flex justify-content-between align-items-start" }
const _hoisted_15 = { class: "flex-grow-1" }
const _hoisted_16 = { class: "card-title mb-1" }
const _hoisted_17 = { class: "card-text text-muted small mb-1" }
const _hoisted_18 = { class: "card-text small" }
const _hoisted_19 = { class: "text-info" }
const _hoisted_20 = ["textContent"]
const _hoisted_21 = { class: "form-check checkbox-wrapper" }
const _hoisted_22 = ["checked", "onClick"]

import { ref, computed, onMounted, watch } from 'vue';
import apiService from '@/services/apiService';

interface Variable {
  _id: string;
  title: string;
  description: string;
  slug: string;
  category_title?: string;
  prompt_category_id?: string;
}

interface Category {
  _id: string;
  title: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VariablePicker',
  props: {
    modelValue: {},
    currentCategoryId: {},
    variables: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Local state
const searchTerm = ref('');
const selectedCategory = ref(props.currentCategoryId || '');
const availableVariables = ref<Variable[]>([]);
const categories = ref<Category[]>([]);
const selectedVariables = ref<Variable[]>([...props.modelValue]);

// Computed
const filteredVariables = computed(() => {
  let filtered = availableVariables.value;

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(v => v.prompt_category_id === selectedCategory.value);
  }

  // Filter by search term
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    filtered = filtered.filter(v => 
      (v.title?.toLowerCase().includes(term) || false) ||
      (v.description?.toLowerCase().includes(term) || false) ||
      (v.slug?.toLowerCase().includes(term) || false)
    );
  }

  return filtered;
});

// Methods
const isSelected = (variableId: string): boolean => {
  return selectedVariables.value.some(v => v._id === variableId);
};

const toggleVariable = (variable: Variable) => {
  console.log('🚀 toggleVariable called with:', {
    id: variable._id,
    title: variable.title,
    slug: variable.slug,
    fullVariable: variable
  });
  
  const index = selectedVariables.value.findIndex(v => v._id === variable._id);
  
  if (index > -1) {
    // Remove variable
    console.log('❌ Removing variable:', variable.title);
    selectedVariables.value.splice(index, 1);
  } else {
    // Add variable
    console.log('✅ Adding variable:', variable.title, 'with slug:', variable.slug);
    selectedVariables.value.push(variable);
  }
  
  console.log('📤 Emitting updated selectedVariables:', [...selectedVariables.value]);
  console.log('📤 Selected variables count:', selectedVariables.value.length);
  
  emit('update:modelValue', [...selectedVariables.value]);
};

const removeVariable = (variable: Variable) => {
  const index = selectedVariables.value.findIndex(v => v._id === variable._id);
  if (index > -1) {
    selectedVariables.value.splice(index, 1);
    emit('update:modelValue', [...selectedVariables.value]);
  }
};

// Variables are automatically added to base description when selected via watchers

// Load data
const loadVariables = async () => {
  if (props.variables && props.variables.length > 0) {
    console.log('📦 Using variables from props:', props.variables.length);
    availableVariables.value = props.variables.filter(item => item && typeof item === 'object').map(item => ({
      _id: item._id,
      title: item.title || '',
      description: item.description || '',
      slug: item.slug || '',
      category_title: (item.prompt_category_id && typeof item.prompt_category_id === 'object') 
        ? (item.prompt_category_id as any).title 
        : undefined,
      prompt_category_id: (item.prompt_category_id && typeof item.prompt_category_id === 'object')
        ? (item.prompt_category_id as any)._id
        : item.prompt_category_id
    }));
    return;
  }

  try {
    console.log('📡 Fetching variables from API...');
    const response = await apiService.get('/common/prompt-variables');
    // Handle both wrapped {.data: [...]} and direct array responses
    const data = (response && response.data && Array.isArray(response.data)) 
      ? response.data 
      : (Array.isArray(response) ? response : []);
      
    if (Array.isArray(data)) {
      const validData = data.filter(item => item && typeof item === 'object');
      console.log('✅ Fetched variables count:', validData.length);
      availableVariables.value = validData.map(item => ({
        _id: item._id,
        title: item.title || '',
        description: item.description || '',
        slug: item.slug || '',
        category_title: (item.prompt_category_id && typeof item.prompt_category_id === 'object') 
          ? (item.prompt_category_id as any).title 
          : undefined,
        prompt_category_id: (item.prompt_category_id && typeof item.prompt_category_id === 'object')
          ? (item.prompt_category_id as any)._id
          : item.prompt_category_id
      }));
    } else {
      console.warn('⚠️ API returned no variables or invalid format');
    }
  } catch (error) {
    console.error('❌ Error loading variables:', error);
  }
};

const loadCategories = async () => {
  try {
    const response = await apiService.get('/common/prompt-categories');
    // Handle both wrapped {.data: [...]} and direct array responses
    const data = (response && response.data && Array.isArray(response.data)) 
      ? response.data 
      : (Array.isArray(response) ? response : []);

    if (Array.isArray(data)) {
      categories.value = data.filter(item => item && typeof item === 'object');
    }
  } catch (error) {
    console.error('Error loading categories:', error);
  }
};

// Watch for changes in props
watch(() => props.modelValue, (newValue) => {
  selectedVariables.value = [...newValue];
}, { deep: true });

// Emit selection changes for debugging
watch(() => selectedVariables.value, (newValue, oldValue) => {
  console.log('VariablePicker - selectedVariables changed:', { newValue, oldValue });
}, { deep: true });

watch(() => props.variables, (newValue) => {
  if (newValue) loadVariables();
}, { deep: true });

watch(() => props.currentCategoryId, (newValue) => {
  selectedCategory.value = newValue || '';
});

// Initialize
onMounted(async () => {
  await Promise.all([loadVariables(), loadCategories()]);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
            type: "text",
            class: "form-control",
            placeholder: "Search variables..."
          }, null, 512), [
            [_vModelText, searchTerm.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedCategory).value = $event)),
            class: "form-select"
          }, [
            _cache[2] || (_cache[2] = _createElementVNode("option", { value: "" }, "All Categories", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (category) => {
              return (_openBlock(), _createElementBlock("option", {
                key: category._id,
                value: category._id
              }, _toDisplayString(category.title), 9, _hoisted_6))
            }), 128))
          ], 512), [
            [_vModelSelect, selectedCategory.value]
          ])
        ])
      ])
    ]),
    (selectedVariables.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _cache[3] || (_cache[3] = _createElementVNode("h6", { class: "mb-2" }, "Selected Variables:", -1)),
          _createElementVNode("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedVariables.value, (variable) => {
              return (_openBlock(), _createElementBlock("span", {
                key: variable._id,
                class: "badge bg-primary me-2 mb-2 d-inline-flex align-items-center"
              }, [
                _createTextVNode(_toDisplayString(variable.title) + " ", 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn-close btn-close-white ms-2",
                  onClick: ($event: any) => (removeVariable(variable)),
                  "aria-label": "Remove"
                }, null, 8, _hoisted_9)
              ]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_10, [
      (filteredVariables.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, " No variables found "))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredVariables.value, (variable) => {
        return (_openBlock(), _createElementBlock("div", {
          key: variable._id,
          class: _normalizeClass(["variable-item card mb-2", { 'selected': isSelected(variable._id) }]),
          onClick: ($event: any) => (toggleVariable(variable)),
          style: {"cursor":"pointer"}
        }, [
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("h6", _hoisted_16, _toDisplayString(variable.title), 1),
                _createElementVNode("p", _hoisted_17, " Category: " + _toDisplayString(variable.category_title || 'N/A'), 1),
                _createElementVNode("p", _hoisted_18, _toDisplayString(variable.description), 1),
                _createElementVNode("small", _hoisted_19, [
                  _cache[4] || (_cache[4] = _createTextVNode("Slug: ", -1)),
                  _createElementVNode("span", {
                    textContent: _toDisplayString(variable.slug)
                  }, null, 8, _hoisted_20)
                ])
              ]),
              _createElementVNode("div", _hoisted_21, [
                _createElementVNode("input", {
                  class: "form-check-input",
                  type: "checkbox",
                  checked: isSelected(variable._id),
                  onClick: _withModifiers(($event: any) => (toggleVariable(variable)), ["stop"])
                }, null, 8, _hoisted_22),
                _cache[5] || (_cache[5] = _createElementVNode("label", { class: "form-check-label text-xs" }, " Select ", -1))
              ])
            ])
          ])
        ], 10, _hoisted_12))
      }), 128))
    ])
  ]))
}
}

})